import React from 'react';
import { useMutation } from '@apollo/react-hooks';
import { useAlert } from 'react-alert';

import useIsComDomain from '@src/hooks/useIsComDomain';
import { useAnonymousCart, useCurrentUser } from '@src/hooks';

import { CART_CREATE } from '@components/ProductPage/ProductDetailSummary/queries';
import { cartCreate } from '@components/ProductPage/ProductDetailSummary/__generated__/cartCreate';
import { CART_MERGE, TOKEN_AUTH } from './queries';
import { cartMerge } from '@src/providers/CredentialProvider/__generated__/cartMerge';
import { tokenCreate, tokenCreateVariables, tokenCreate_tokenCreate } from './__generated__/tokenCreate';
import {
  cartCreate_cartCreate_cart,
  cartCreateVariables,
} from '@components/ProductPage/ProductDetailSummary/__generated__/cartCreate';
import { cartLinesAdd_cartLineAdd_cart } from '@components/ProductPage/ProductDetailSummary/__generated__/cartLinesAdd';

import { gaV4Login, gaV4SetUserId, getDBIdFromGraphqlId, getGraphqlIdFromDBId } from '@src/core/utils';

export const CredentialContext = React.createContext(null);

export interface credentialType {
  currentUser: tokenCreate_tokenCreate | null;
  setCurrentUser: (user: tokenCreate_tokenCreate) => void;
  logOut: () => {};
  logIn: (variables: tokenCreateVariables) => void;
  anonymousCart: cartCreate_cartCreate_cart | null;
  setAnonymousCart: (cart: cartCreate_cartCreate_cart | cartLinesAdd_cartLineAdd_cart) => void;
  mgsAnonymousCart: cartCreate_cartCreate_cart | null;
  setMgsAnonymousCart: (cart: cartCreate_cartCreate_cart | cartLinesAdd_cartLineAdd_cart) => void;
  createAndSetAnonymousCart: any;
  currentCart: CurrentCart;
  setCurrentCart: (cart: cartBadgeType) => void;
  mergeCart: any;
  loading: boolean;
}

export interface cartBadgeType {
  __typename: 'Cart';
  token: any;
  quantity: number;
  forMgs: boolean;
}

export interface CurrentCart {
  maccaronCart?: cartCreate_cartCreate_cart | cartLinesAdd_cartLineAdd_cart | null;
  mgsCart?: cartCreate_cartCreate_cart | cartLinesAdd_cartLineAdd_cart | null;
}

export const CredentialProvider: React.FC<{}> = ({ children }) => {
  const isComDomain = useIsComDomain();
  const alert = useAlert();

  //TODO: Separate CartProvider
  const { currentUser, setCurrentUser, logOut } = useCurrentUser();
  const {
    anonymousCart,
    setAnonymousCart,
    removeAnonymousCart,
    mgsAnonymousCart,
    setMgsAnonymousCart,
    removeMgsAnonymousCart,
  } = useAnonymousCart();

  const setUserCart = (cart: cartBadgeType) => {
    // cart forMgs 여부에 따라 user의 cart 정보 업데이트
    const newUser = {
      ...currentUser?.user,
      mgsCart: cart?.forMgs ? cart : currentUser?.user?.mgsCart,
      maccaronCart: cart?.forMgs ? currentUser?.user?.maccaronCart : cart,
    };

    setCurrentUser({
      ...currentUser,
      user: newUser,
    });
  };

  const currentCart = {
    maccaronCart: currentUser ? currentUser.user?.maccaronCart : anonymousCart,
    mgsCart: currentUser ? currentUser.user?.mgsCart : mgsAnonymousCart,
  };

  const setCurrentCart = (cart: cartBadgeType) => {
    if (currentUser) {
      setUserCart(cart);
    } else {
      if (cart?.forMgs) {
        setMgsAnonymousCart(cart);
      } else {
        setAnonymousCart(cart);
      }
    }
  };

  const [cartCreate] = useMutation<cartCreate>(CART_CREATE);

  const [mergeCart, { loading: cartMergeLoading }] = useMutation<cartMerge>(CART_MERGE, {
    onCompleted: (data) => {
      //remove anonymous cart if merging succeeded
      const res = data?.cartMerge?.cart;
      setUserCart(res);

      if (res?.forMgs) {
        removeMgsAnonymousCart();
      } else {
        removeAnonymousCart();
      }
    },
  });

  const createAndSetAnonymousCart = async (variables: cartCreateVariables, doCartMerge: boolean) => {
    const res = await cartCreate({ variables });
    const response = res?.data?.cartCreate?.cart;

    if (variables?.forMgs) {
      setMgsAnonymousCart(response);
    } else {
      setAnonymousCart(response);
    }

    if (response && doCartMerge) {
      const cartId = getGraphqlIdFromDBId(response?.token, 'Cart');
      await mergeCart({ variables: { cartId } });
    }

    return response;
  };

  const [createToken, { loading }] = useMutation<tokenCreate>(TOKEN_AUTH, {
    fetchPolicy: 'no-cache',
    onCompleted: async (data) => {
      const tokenCreateResultData = data?.tokenCreate;
      const errors = tokenCreateResultData?.errors;

      if (errors?.length === 0) {
        setCurrentUser(tokenCreateResultData);

        // forMgs에 따라 merge
        if (anonymousCart && mgsAnonymousCart) {
          const maccaronAnonymousCartId = getGraphqlIdFromDBId(anonymousCart.token, 'Cart');
          const mgsAnonymousCartId = getGraphqlIdFromDBId(mgsAnonymousCart.token, 'Cart');

          await mergeCart({ variables: { cartId: maccaronAnonymousCartId } });
          await mergeCart({ variables: { cartId: mgsAnonymousCartId } });
        } else {
          if (!anonymousCart) {
            await createAndSetAnonymousCart({ forMgs: false }, true);
          }
          if (!mgsAnonymousCart) {
            await createAndSetAnonymousCart({ forMgs: true }, true);
          }
        }

        await gaV4SetUserId();
        const userId = getDBIdFromGraphqlId(tokenCreateResultData?.user?.id);
        gaV4Login('Email', userId?.toString());
      } else {
        alert.error('Please enter a correct email and password. ' + 'Note that both fields may be case-sensitive.');
      }
    },
  });

  const logIn = (variables: tokenCreateVariables) => {
    createToken({ variables });
  };

  const credentialValue: credentialType = {
    currentUser,
    setCurrentUser,
    logOut,
    logIn,
    anonymousCart,
    setAnonymousCart,
    mgsAnonymousCart,
    setMgsAnonymousCart,
    createAndSetAnonymousCart,
    currentCart,
    setCurrentCart,
    mergeCart,
    loading: loading || cartMergeLoading,
  };

  return <CredentialContext.Provider value={credentialValue}>{children}</CredentialContext.Provider>;
};
